import { existsSync } from "node:fs";
import { readFile } from "node:fs/promises";
import path, { join } from "node:path";
import { fileURLToPath } from "node:url";

const CLI_PACKAGE_JSON = "package.json";
const WAKELOOP_CLI_SKILL_ID = "wakeloop-cli";
const SKILL_FILE_NAME = "SKILL.md";

export type BundledSkillsLocation = {
  cliRootDir: string;
  skillsRootDir: string;
};

export function resolveBundledSkillsLocation(): BundledSkillsLocation | null {
  const moduleDir = path.dirname(fileURLToPath(import.meta.url));
  const cliRootDir = resolveCliRootDir(moduleDir);
  if (!cliRootDir) {
    return null;
  }
  return {
    cliRootDir,
    skillsRootDir: path.join(cliRootDir, "skills"),
  };
}

export function resolveBundledWakeLoopCliSkillFilePath(): string {
  return resolveBundledWakeLoopCliSkillDocumentPath(SKILL_FILE_NAME);
}

export function resolveBundledWakeLoopCliSkillDocumentPath(
  relativePath: string
): string {
  const bundledSkills = resolveBundledSkillsLocation();
  if (!bundledSkills) {
    throw new Error("unable to resolve bundled WakeLoop skills directory");
  }
  const skillRootDir = join(bundledSkills.skillsRootDir, WAKELOOP_CLI_SKILL_ID);
  const candidatePath = path.resolve(skillRootDir, relativePath);
  if (
    !candidatePath.startsWith(`${skillRootDir}${path.sep}`) &&
    candidatePath !== skillRootDir
  ) {
    throw new Error(`invalid bundled WakeLoop CLI skill path: ${relativePath}`);
  }
  return candidatePath;
}

export async function readBundledWakeLoopCliSkillDocument(): Promise<string> {
  const skillFilePath = resolveBundledWakeLoopCliSkillFilePath();
  return await readBundledWakeLoopCliSkillFile(skillFilePath);
}

export async function readBundledWakeLoopCliSkillDocumentByPath(
  relativePath: string
): Promise<string> {
  return await readBundledWakeLoopCliSkillFile(
    resolveBundledWakeLoopCliSkillDocumentPath(relativePath)
  );
}

async function readBundledWakeLoopCliSkillFile(
  skillFilePath: string
): Promise<string> {
  const raw = await readFile(skillFilePath, "utf8").catch(() => "");
  if (!raw) {
    throw new Error(`bundled WakeLoop CLI skill is missing: ${skillFilePath}`);
  }
  return raw;
}

function resolveCliRootDir(fromDir: string): string | null {
  let current = fromDir;
  for (;;) {
    const packageJsonPath = path.join(current, CLI_PACKAGE_JSON);
    const hasPackageJson = existsSync(packageJsonPath);
    const hasSkillsDir = existsSync(path.join(current, "skills"));
    if (hasPackageJson && hasSkillsDir) {
      return current;
    }
    const parent = path.dirname(current);
    if (parent === current) {
      return null;
    }
    current = parent;
  }
}
